import {useCallback, useState} from 'react'

import {PlannerLSSettingsKeys, PlannerLocalStorageSettingsSchema} from '../types'

const readSetting = <K extends PlannerLSSettingsKeys>(
  key: K
): PlannerLocalStorageSettingsSchema[K] | undefined => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as PlannerLocalStorageSettingsSchema[K]) : undefined
  } catch {
    return undefined
  }
}

const writeSetting = <K extends PlannerLSSettingsKeys>(
  key: K,
  value: PlannerLocalStorageSettingsSchema[K]
): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // ignore write failures (e.g. quota exceeded / private mode) — graceful degradation
  }
}

export const usePersistingSettingState = <K extends PlannerLSSettingsKeys>(
  key: K,
  initial: PlannerLocalStorageSettingsSchema[K]
) => {
  const [state, setState] = useState<PlannerLocalStorageSettingsSchema[K]>(
    () => readSetting(key) ?? initial
  )

  const updateState = useCallback(
    (newState: PlannerLocalStorageSettingsSchema[K]) => {
      writeSetting(key, newState)
      setState(newState)
    },
    [key]
  )

  return [state, updateState] as const
}
